/**
 * Admin User Activity Page
 * Unified view of which users paid, posted jobs, or placed bids.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "@/context/AppContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  ArrowLeft, DollarSign, Briefcase, Gavel, Users as UsersIcon, Loader2, Search,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminUserActivity = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");

  const fetchData = async (activeFilter = filter, q = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeFilter !== "all") params.set("activity", activeFilter);
      if (q) params.set("search", q);
      params.set("limit", "100");
      const res = await axios.get(`${API}/admin/user-activity?${params.toString()}`, {
        withCredentials: true,
      });
      setRows(res.data.rows || []);
      setSummary(res.data.summary || null);
    } catch (err) {
      console.error("Failed to load activity:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData("all", "");
  }, []);

  const onFilter = (val) => {
    setFilter(val);
    fetchData(val, search);
  };

  const onSearch = (e) => {
    e.preventDefault();
    fetchData(filter, search);
  };

  const formatDate = (s) => (s ? new Date(s).toLocaleDateString("de-CH") : "—");
  const fmtChf = (n) => `CHF ${Number(n || 0).toFixed(2)}`;

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8" data-testid="admin-user-activity-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate("/admin")}
              data-testid="back-to-admin-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">
              {t("userActivity") || "User Activity"}
            </h1>
          </div>
        </div>

        {/* Summary KPIs */}
        {summary && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Card data-testid="kpi-paying-users">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-500 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> {t("payingUsers") || "Paying users"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{summary.paying_users}</div>
              </CardContent>
            </Card>
            <Card data-testid="kpi-revenue">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-500 flex items-center gap-2">
                  <DollarSign className="w-4 h-4" /> {t("totalRevenue") || "Total revenue"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{fmtChf(summary.total_revenue_chf)}</div>
              </CardContent>
            </Card>
            <Card data-testid="kpi-job-posters">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-500 flex items-center gap-2">
                  <Briefcase className="w-4 h-4" /> {t("usersWithJobs") || "Users who posted jobs"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{summary.users_with_jobs}</div>
              </CardContent>
            </Card>
            <Card data-testid="kpi-bidders">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm text-gray-500 flex items-center gap-2">
                  <Gavel className="w-4 h-4" /> {t("usersWithBids") || "Users who placed bids"}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{summary.users_with_bids}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4 flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
            <Tabs value={filter} onValueChange={onFilter} className="w-full sm:w-auto">
              <TabsList data-testid="activity-filter-tabs">
                <TabsTrigger value="all" data-testid="filter-all">
                  <UsersIcon className="w-4 h-4 mr-1" /> {t("allUsers") || "All"}
                </TabsTrigger>
                <TabsTrigger value="paid" data-testid="filter-paid">
                  <DollarSign className="w-4 h-4 mr-1" /> {t("paid") || "Paid"}
                </TabsTrigger>
                <TabsTrigger value="posted_jobs" data-testid="filter-jobs">
                  <Briefcase className="w-4 h-4 mr-1" /> {t("postedJobs") || "Posted Jobs"}
                </TabsTrigger>
                <TabsTrigger value="placed_bids" data-testid="filter-bids">
                  <Gavel className="w-4 h-4 mr-1" /> {t("placedBids") || "Placed Bids"}
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <form onSubmit={onSearch} className="flex gap-2 flex-1 sm:max-w-sm sm:ml-auto">
              <Input
                placeholder={t("searchByNameEmail") || "Search by name or email"}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="activity-search-input"
              />
              <Button type="submit" size="icon" data-testid="activity-search-btn">
                <Search className="w-4 h-4" />
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 flex justify-center">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : rows.length === 0 ? (
              <div className="p-12 text-center text-gray-500" data-testid="no-activity-empty">
                {t("noUsersFound") || "No users found."}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="activity-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("user") || "User"}</TableHead>
                      <TableHead>{t("role") || "Role"}</TableHead>
                      <TableHead>{t("subscription") || "Subscription"}</TableHead>
                      <TableHead className="text-right">{t("totalPaid") || "Total Paid"}</TableHead>
                      <TableHead className="text-right">{t("jobs") || "Jobs"}</TableHead>
                      <TableHead className="text-right">{t("bids") || "Bids"}</TableHead>
                      <TableHead>{t("lastActivity") || "Last Activity"}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => {
                      const lastActivity = [r.last_payment_at, r.last_job_at, r.last_bid_at]
                        .filter(Boolean)
                        .sort()
                        .pop();
                      return (
                        <TableRow
                          key={r.user_id}
                          className="cursor-pointer hover:bg-gray-50"
                          onClick={() => navigate(`/admin/users/${r.user_id}`)}
                          data-testid={`activity-row-${r.user_id}`}
                        >
                          <TableCell>
                            <div className="font-medium">{r.name || "—"}</div>
                            <div className="text-xs text-gray-500">{r.email}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{r.role}</Badge>
                          </TableCell>
                          <TableCell>
                            {r.subscription ? (
                              <Badge className="bg-emerald-100 text-emerald-700">
                                {r.subscription} {r.subscription_status === "paused" ? "(paused)" : ""}
                              </Badge>
                            ) : (
                              <span className="text-gray-400 text-sm">{t("none") || "None"}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {r.total_paid_chf > 0 ? fmtChf(r.total_paid_chf) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {r.jobs_count > 0 ? r.jobs_count : <span className="text-gray-300">—</span>}
                          </TableCell>
                          <TableCell className="text-right">
                            {r.bids_count > 0 ? r.bids_count : <span className="text-gray-300">—</span>}
                          </TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {formatDate(lastActivity)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminUserActivity;
